/**
 * Centralized Calendar Configuration
 *
 * This file contains all calendar-related configuration constants to ensure
 * consistency across all calendar views and components.
 */

export interface CalendarDisplayConfig {
  // Time column configuration
  timeColumn: {
    width: string; // Tailwind classes for responsive width
    mobileWidth: number; // Width in pixels for mobile
    desktopWidth: number; // Width in pixels for desktop
  };

  // Time formatting configuration
  timeFormat: {
    use24Hour: boolean;
    showMinutes: boolean;
    luxonFormat: string; // Luxon format string
  };

  // Grid layout configuration
  layout: {
    hourHeight: number; // Height per hour in pixels
    minAppointmentHeight: number; // Minimum appointment height in pixels
    headerHeight: number; // Header height in pixels
  };

  // Responsive breakpoints
  breakpoints: {
    mobile: string; // Tailwind breakpoint
    tablet: string; // Tailwind breakpoint
    desktop: string; // Tailwind breakpoint
  };
}

/**
 * Default calendar configuration
 * Single source of truth for all calendar display settings
 */
export const CALENDAR_CONFIG: CalendarDisplayConfig = {
  timeColumn: {
    width: "w-10 sm:w-12", // w-10 (40px) on mobile, w-12 (48px) on desktop
    mobileWidth: 40, // 2.5rem = 40px
    desktopWidth: 48, // 3rem = 48px
  },

  timeFormat: {
    use24Hour: true,
    showMinutes: true,
    luxonFormat: "HH:mm", // 24-hour format
  },

  layout: {
    hourHeight: 48, // 48px per hour (12px per 15-minute interval)
    minAppointmentHeight: 24, // Minimum 24px height for readability
    headerHeight: 48, // 48px header height
  },

  breakpoints: {
    mobile: "sm:", // Small screens and up
    tablet: "md:", // Medium screens and up
    desktop: "lg:", // Large screens and up
  },
};

/**
 * Time slot configuration for calendar grids
 */
export interface TimeSlotConfig {
  startHour: number;
  endHour: number;
  hourHeight: number;
  showMinutes?: boolean;
  format12Hour?: boolean;
}

/**
 * Create time slot configuration using centralized settings
 */
export function createTimeSlotConfig(
  startHour: number = 0,
  endHour: number = 24,
  overrides?: Partial<TimeSlotConfig>
): TimeSlotConfig {
  return {
    startHour,
    endHour,
    hourHeight: CALENDAR_CONFIG.layout.hourHeight,
    showMinutes: CALENDAR_CONFIG.timeFormat.showMinutes,
    format12Hour: !CALENDAR_CONFIG.timeFormat.use24Hour,
    ...overrides,
  };
}

/**
 * Get CSS grid column template for calendar layout
 * Note: This function is deprecated. Use direct width classes on TimeColumn instead.
 */
export function getCalendarGridColumns(): string {
  const { mobileWidth, desktopWidth } = CALENDAR_CONFIG.timeColumn;
  return `grid-cols-[${mobileWidth}px_1fr] ${CALENDAR_CONFIG.breakpoints.mobile}grid-cols-[${desktopWidth}px_1fr]`;
}

/**
 * Get time column width classes for Tailwind
 */
export function getTimeColumnWidth(): string {
  return CALENDAR_CONFIG.timeColumn.width;
}

/**
 * Get time format configuration
 */
export function getTimeFormat(): string {
  return CALENDAR_CONFIG.timeFormat.luxonFormat;
}

/**
 * Get layout configuration
 */
export function getLayoutConfig() {
  return CALENDAR_CONFIG.layout;
}

/**
 * Scale definitions - modify these values to change scale behavior
 * Easy to adjust for different zoom levels and requirements
 */
export const SCALE_DEFINITIONS = {
  default: {
    hourHeight: 60, // Pixels per hour (was 'expanded')
    minAppointmentHeight: 30, // Minimum appointment height
    description: "Standard view",
    pixelsPerMinute: 1.0, // Calculated: hourHeight / 60
  },
  // expanded: {
  //   hourHeight: 80,           // Pixels per hour
  //   minAppointmentHeight: 40, // Minimum appointment height
  //   description: 'Comfortable view',
  //   pixelsPerMinute: 1.33,    // Calculated: hourHeight / 60
  // },
  detailed: {
    hourHeight: 120, // Pixels per hour
    minAppointmentHeight: 60, // Minimum appointment height
    description: "Short appointments",
    pixelsPerMinute: 2.0, // Calculated: hourHeight / 60
  },
} as const;

/**
 * Calendar theme variants built from scale definitions
 * These are automatically generated from SCALE_DEFINITIONS above
 */
export const CALENDAR_THEMES = {
  default: {
    ...CALENDAR_CONFIG,
    layout: {
      ...CALENDAR_CONFIG.layout,
      hourHeight: SCALE_DEFINITIONS.default.hourHeight,
      minAppointmentHeight: SCALE_DEFINITIONS.default.minAppointmentHeight,
    },
  },
  // expanded: {
  //   ...CALENDAR_CONFIG,
  //   layout: {
  //     ...CALENDAR_CONFIG.layout,
  //     hourHeight: SCALE_DEFINITIONS.expanded.hourHeight,
  //     minAppointmentHeight: SCALE_DEFINITIONS.expanded.minAppointmentHeight,
  //   },
  // },
  detailed: {
    ...CALENDAR_CONFIG,
    layout: {
      ...CALENDAR_CONFIG.layout,
      hourHeight: SCALE_DEFINITIONS.detailed.hourHeight,
      minAppointmentHeight: SCALE_DEFINITIONS.detailed.minAppointmentHeight,
    },
  },
} as const;

export type CalendarTheme = keyof typeof CALENDAR_THEMES;

/**
 * Get configuration for a specific theme
 */
export function getCalendarThemeConfig(theme: CalendarTheme = "default"): CalendarDisplayConfig {
  return CALENDAR_THEMES[theme];
}
